/**
 * TanStack Query Hooks for Folder Queries
 *
 * Part of the Data Workspace OS feature set.
 * Provides read-only access to folder data with:
 * - Automatic caching and deduplication
 * - Background refetching
 * - Stale time management
 *
 * @module features/organization/hooks/useFolderQueries
 */

import { useQuery } from '@tanstack/react-query';
import type { FolderTreeNode, FolderWithCounts, TrashItems } from '@loader/shared-types';

import { useRpc } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all folders in the workspace (flat list)
 *
 * Returns folders with counts for tables, linked tables, and subfolders.
 * Useful for folder selection dropdowns and management views.
 */
export function useFoldersQuery(options?: { enabled?: boolean }) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.organization.folders.list(),
    queryFn: async () => {
      const res = await rpc.api.private.organization.folders.$get();

      if (!res.ok) {
        throw new Error('Failed to fetch folders');
      }

      const data = await res.json();
      return data.folders as FolderWithCounts[];
    },
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch folder tree structure (hierarchical)
 *
 * Returns nested folder structure for sidebar navigation.
 * Each node contains children array for recursive rendering.
 */
export function useFolderTreeQuery(options?: { enabled?: boolean }) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.organization.folders.tree,
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders.tree.$get();

      if (!res.ok) {
        throw new Error('Failed to fetch folder tree');
      }

      const data = await res.json();
      return data.tree as FolderTreeNode[];
    },
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single folder by ID
 *
 * Includes counts for contained items.
 */
export function useFolderQuery(id: string | undefined, options?: { enabled?: boolean }) {
  const rpc = useRpc();

  return useQuery({
    queryKey: id
      ? queryKeys.organization.folders.detail(id)
      : ['organization', 'folders', 'detail', 'disabled'],
    queryFn: async () => {
      if (!id) throw new Error('Folder ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders[':id'].$get({
        param: { id },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch folder');
      }

      const data = await res.json();
      return data.folder as FolderWithCounts;
    },
    enabled: !!id && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch items in trash (soft-deleted)
 *
 * Returns all trashed folders, tables, and linked tables with parent references.
 * Filtering by folder is done client-side for instant navigation.
 */
export function useTrashItemsQuery(options?: { enabled?: boolean }) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.organization.folders.trash,
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders.trash.$get();

      if (!res.ok) {
        throw new Error('Failed to fetch trash items');
      }

      const data = await res.json();
      return data as TrashItems;
    },
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
